import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import productData from "../Datas/productData";

const CART_STORAGE_KEY = "rumnwillow-cart-v1";
const WISHLIST_STORAGE_KEY = "rumnwillow-wishlist-v1";
const MAX_ITEM_QUANTITY = 10;
const VALID_PRODUCT_IDS = new Set(productData.map((product) => Number(product.id)));

const readStoredValue = (key, fallback) => {
  if (typeof window === "undefined") return fallback;

  try {
    const storedValue = window.localStorage.getItem(key);
    return storedValue ? JSON.parse(storedValue) : fallback;
  } catch {
    return fallback;
  }
};

const persistValue = (key, value) => {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Shopping remains fully usable when storage is unavailable.
  }
};

const normalizeQuantity = (quantity) => {
  const parsedQuantity = Number.parseInt(quantity, 10);
  return Number.isFinite(parsedQuantity)
    ? Math.min(MAX_ITEM_QUANTITY, Math.max(1, parsedQuantity))
    : 1;
};

const normalizeCartItems = (value) => {
  if (!Array.isArray(value)) return [];

  return value
    .filter((item) => item && VALID_PRODUCT_IDS.has(Number(item.id)))
    .map((item) => ({
      id: Number(item.id),
      size: String(item.size || "Standard"),
      color: String(item.color || "Original"),
      quantity: normalizeQuantity(item.quantity),
    }));
};

const normalizeWishlist = (value) => {
  if (!Array.isArray(value)) return [];
  return [...new Set(value.map(Number).filter((id) => VALID_PRODUCT_IDS.has(id)))];
};

const defaultContextValue = {
  cartItems: [],
  cartCount: 0,
  wishlist: [],
  addToCart: () => {},
  addCart: () => {},
  removeFromCart: () => {},
  removeCart: () => {},
  updateQuantity: () => {},
  clearCart: () => {},
  addWishlist: () => {},
  removeWishlist: () => {},
  toggleWishlist: () => {},
  isWishlisted: () => false,
};

export const CartContext = createContext(defaultContextValue);

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() => {
    const storedCart = readStoredValue(CART_STORAGE_KEY, null);
    if (Array.isArray(storedCart)) return normalizeCartItems(storedCart);

    const legacyCart = readStoredValue("cartItems", []);
    return normalizeCartItems(legacyCart);
  });
  const [wishlist, setWishlist] = useState(() => {
    const storedWishlist = readStoredValue(WISHLIST_STORAGE_KEY, []);
    return normalizeWishlist(storedWishlist);
  });

  useEffect(() => {
    persistValue(CART_STORAGE_KEY, cartItems);
  }, [cartItems]);

  useEffect(() => {
    persistValue(WISHLIST_STORAGE_KEY, wishlist);
  }, [wishlist]);

  const addToCart = useCallback((productId, quantity = 1, size, color) => {
    const normalizedId = Number(productId);
    if (!VALID_PRODUCT_IDS.has(normalizedId)) return;
    const normalizedSize = size || "Standard";
    const normalizedColor = color || "Original";
    const normalizedQuantity = normalizeQuantity(quantity);

    setCartItems((currentItems) => {
      const matchingItem = currentItems.find(
        (item) =>
          Number(item.id) === normalizedId &&
          item.size === normalizedSize &&
          item.color === normalizedColor
      );

      if (!matchingItem) {
        return [
          ...currentItems,
          {
            id: normalizedId,
            size: normalizedSize,
            color: normalizedColor,
            quantity: normalizedQuantity,
          },
        ];
      }

      return currentItems.map((item) =>
        Number(item.id) === normalizedId &&
        item.size === normalizedSize &&
        item.color === normalizedColor
          ? {
              ...item,
              quantity: Math.min(
                MAX_ITEM_QUANTITY,
                normalizeQuantity(item.quantity) + normalizedQuantity
              ),
            }
          : item
      );
    });
  }, []);

  const removeFromCart = useCallback((productId, size, color) => {
    setCartItems((currentItems) =>
      currentItems.filter(
        (item) =>
          !(
            Number(item.id) === Number(productId) &&
            item.size === size &&
            item.color === color
          )
      )
    );
  }, []);

  const updateQuantity = useCallback((productId, size, color, quantity) => {
    setCartItems((currentItems) =>
      currentItems.map((item) =>
        Number(item.id) === Number(productId) &&
        item.size === size &&
        item.color === color
          ? { ...item, quantity: normalizeQuantity(quantity) }
          : item
      )
    );
  }, []);

  const clearCart = useCallback(() => setCartItems([]), []);

  const addWishlist = useCallback((productId) => {
    const normalizedId = Number(productId);
    if (!VALID_PRODUCT_IDS.has(normalizedId)) return;
    setWishlist((currentWishlist) =>
      currentWishlist.includes(normalizedId)
        ? currentWishlist
        : [...currentWishlist, normalizedId]
    );
  }, []);

  const removeWishlist = useCallback((productId) => {
    setWishlist((currentWishlist) =>
      currentWishlist.filter((id) => id !== Number(productId))
    );
  }, []);

  const toggleWishlist = useCallback((productId) => {
    const normalizedId = Number(productId);
    if (!VALID_PRODUCT_IDS.has(normalizedId)) return;
    setWishlist((currentWishlist) =>
      currentWishlist.includes(normalizedId)
        ? currentWishlist.filter((id) => id !== normalizedId)
        : [...currentWishlist, normalizedId]
    );
  }, []);

  const isWishlisted = useCallback(
    (productId) => wishlist.includes(Number(productId)),
    [wishlist]
  );

  const cartCount = useMemo(
    () =>
      cartItems.reduce(
        (total, item) => total + normalizeQuantity(item.quantity),
        0
      ),
    [cartItems]
  );

  const contextValue = useMemo(
    () => ({
      cartItems,
      cartCount,
      wishlist,
      addToCart,
      addCart: addToCart,
      removeFromCart,
      removeCart: removeFromCart,
      updateQuantity,
      clearCart,
      addWishlist,
      removeWishlist,
      toggleWishlist,
      isWishlisted,
    }),
    [
      addToCart,
      addWishlist,
      cartCount,
      cartItems,
      clearCart,
      isWishlisted,
      removeFromCart,
      removeWishlist,
      toggleWishlist,
      updateQuantity,
      wishlist,
    ]
  );

  return (
    <CartContext.Provider value={contextValue}>{children}</CartContext.Provider>
  );
};
